// src/constants/grid.ts
var BREAKPOINTS = { "grid-md": 840, "grid-xs": 0 };
var ITEMS_TYPES = {
  CELL: "cell",
  HGRID: "hgrid",
  VGRID: "vgrid",
  TABS: "tabs",
  TAB: "tab",
  WORKPLAN: "workplan",
  WPCARD: "wpcard"
};

// src/constants/layout.ts
var FIRST_XS_LAYOUT_WITH_DND = 1;
var FIRST_MD_LAYOUT_WITH_DND = 3;
var SCREENMODE_DEPTH = {
  "grid-xs": FIRST_XS_LAYOUT_WITH_DND,
  "grid-md": FIRST_MD_LAYOUT_WITH_DND
};

// src/constants/tag.ts
var QUESTION_TAGS = [
  "qt",
  "qtarea",
  "qs",
  "qsmath",
  "qr",
  "qc",
  "qm",
  "qmath",
  "draganddrop",
  "dnd",
  "qdraw",
  "qdraw2",
  "geogebra"
];

// src/utils/dom.ts
import _ from "lodash/fp.js";
import { Children } from "react";

// src/constants/contentMd.ts
var CONTENT_MD_DATA_ATTRIBUTE_PREFIX = "data-content-md-";
var CONTENT_MD_DATA_ATTRIBUTE_TAG = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}tag`;
var CONTENT_MD_DATA_ATTRIBUTE_UUID = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}uuid`;
var CONTENT_MD_DATA_ATTRIBUTE_DOC_ID = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}doc-id`;
var CONTENT_MD_DATA_ATTRIBUTE_PREFIX_REGEXP = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}*`;
var CONTENT_MD_DATA_ATTRIBUTE_QRICH_ID = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}qrich-id`;
var CONTENT_MD_TAG_NO_TEXT = "notext";
var CONTENT_MD_TAG_BACKUP = "mdbackup";
var CONTENT_MD_ATTR_SERIALIZE = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}lightserialize`;
var CONTENT_MD_DATA_KEEP_PROPAGATION = `${CONTENT_MD_DATA_ATTRIBUTE_PREFIX}keep-propagation`;

// src/utils/dom.ts
var isSsr = () => typeof window === "undefined";
var getWindow = () => isSsr() ? { getSelection: () => null } : window;
var isPassiveSupported = _.once(() => {
  let supportsPassive = false;
  let opts = void 0;
  try {
    opts = Object.defineProperty({}, "passive", {
      get: () => {
        supportsPassive = true;
        return true;
      }
    });
    window.addEventListener("test", _.noop, opts);
  } catch (_e) {
  } finally {
    window.removeEventListener("test", _.noop, opts);
  }
  return supportsPassive;
});
var findParent = _.curry(
  (fn, current) => {
    while (current) {
      if (fn(current)) {
        return current;
      }
      if (_.invokeArgs("hasAttribute", [CONTENT_MD_DATA_ATTRIBUTE_DOC_ID], current)) {
        return void 0;
      }
      current = current.parentNode;
    }
  }
);
var findChild = _.curry(
  (fn, current) => {
    if (!current) {
      return;
    }
    if (fn(current)) {
      return current;
    }
    if (_.isUndefined(current.children)) {
      return;
    }
    for (const child of current.children) {
      const found = findChild(fn, child);
      if (found) {
        return found;
      }
    }
  }
);
var isInsideContentMd = _.flow(
  findParent(_.invokeArgs("hasAttribute", [CONTENT_MD_DATA_ATTRIBUTE_DOC_ID])),
  Boolean
);
var isInsideQrich = _.flow(
  findParent(_.invokeArgs("hasAttribute", [CONTENT_MD_DATA_ATTRIBUTE_QRICH_ID])),
  Boolean
);
var getWhichContentMd = _.flow(
  findParent(_.invokeArgs("hasAttribute", [CONTENT_MD_DATA_ATTRIBUTE_DOC_ID])),
  (element) => _.invokeArgs("getAttribute", [CONTENT_MD_DATA_ATTRIBUTE_UUID], element) || _.invokeArgs("getAttribute", [CONTENT_MD_DATA_ATTRIBUTE_DOC_ID], element)
);
var matchesSpanTag = _.curry((tag, element) => {
  return element.tagName === "SPAN" && element.getAttribute(CONTENT_MD_DATA_ATTRIBUTE_TAG) === tag;
});
var hasTag = _.curry((tag, node) => {
  const isElement = (node2) => !!node2 && "tagName" in node2;
  return isElement(node) && node.tagName.localeCompare(tag, "en", { sensitivity: "base" }) === 0 || // in case the tagname is not an html5 one, rely on the data attribute
  _.invokeArgs("getAttribute", [CONTENT_MD_DATA_ATTRIBUTE_TAG], node) === tag;
});
var hasAttributeTag = _.curry((tag, node) => {
  return _.invokeArgs("getAttribute", [CONTENT_MD_DATA_ATTRIBUTE_TAG], node) === tag;
});
var hasClassName = _.curry((className, node) => {
  return _.invokeArgs("classList.contains", [className], node);
});
var escapeAttribute = (str) => {
  if (!_.isString(str)) return "";
  const map = {
    "&": "&amp;",
    "'": "&apos;",
    '"': "&quot;"
  };
  return str.replace(/[&'"]/g, (passage) => map[passage]);
};
var omitWarningProps = _.omit([
  "showTooltip",
  "areQuestionsDisabled",
  "docJson",
  "screenMode",
  "showcorrection",
  "isright",
  "areRightResponsesShown",
  "displayName",
  "isContentEditable",
  "isInsideSession",
  "clientFetch",
  "isdropped",
  "onConnect"
]);

// src/utils/layout.ts
import _3 from "lodash/fp.js";

// src/utils/layoutTemplate.ts
import _2 from "lodash/fp.js";

// src/utils/layout.ts
var getUniqueId = () => _3.uniqueId(`${Date.now()}_`);
var findParentLayout = (uuid, layout) => {
  if (_3.isEmpty(layout)) {
    return void 0;
  }
  if (typeof layout?.content !== "undefined") {
    for (let i = 0; i < layout.content.length; i++) {
      const childComp = layout.content[i];
      if (childComp?.id === uuid) {
        return [layout, i];
      }
      if (childComp?.type !== "cell") {
        const parentLayout = findParentLayout(uuid, childComp);
        if (parentLayout !== void 0) return parentLayout;
      }
    }
  }
  return void 0;
};
var checkLayoutAndUuid = (templateOrLayout, uuid) => {
  if (_3.isEmpty(templateOrLayout)) {
    return { check: true, value: {} };
  }
  if (!uuid || !_3.isString(uuid)) {
    return { check: true, value: templateOrLayout };
  }
  return { check: false, value: {} };
};
var findSiblingsAndRemoveWidth = (uuid, layout) => {
  const { check, value } = checkLayoutAndUuid(layout, uuid);
  if (check) return value;
  const parentLayout = findParentLayout(uuid, layout);
  if (!parentLayout) return layout;
  const parent = parentLayout[0];
  const layoutPath = getLayoutPath({ uuid: parent.id, layouts: layout });
  const subPath = _3.isEmpty(layoutPath) ? "content" : `${subStringPath({ layoutPath })}.content`;
  const content = _3.flatMap((child) => child.id === uuid ? child : _3.unset("w", child), parent.content);
  return _3.set(`${subPath}`, content, layout);
};
var findParentLayoutId = ({
  uuid,
  layouts
}) => {
  if (_3.isEmpty(layouts) || !uuid || !_3.isString(uuid)) {
    return [void 0, void 0];
  }
  const parentLayout = findParentLayout(uuid, layouts);
  if (!parentLayout) return [void 0, void 0];
  const [layout, index] = parentLayout;
  return [_3.getOr("", "id", layout), index + 1];
};
var getFirstLayoutId = ({ layouts }) => {
  return _3.get("content.0.id", layouts);
};
var findLayout = ({
  layouts,
  matchFn = _3.constant(false)
} = {}) => {
  if (_3.isEmpty(layouts)) return;
  if (matchFn(layouts)) return layouts;
  if (layouts.type === "cell") return;
  for (const el of layouts.content ?? []) {
    const back = findLayout({ layouts: el, matchFn });
    if (back) return back;
  }
};
var subStringPath = ({ layoutPath }) => {
  return _3.isEmpty(layoutPath) ? "" : `content.${_3.join(".content.", layoutPath)}`;
};
var getLayoutPath = ({
  uuid,
  layouts
}) => {
  if (_3.isNil(uuid) || uuid === "") return [];
  let path = [];
  _3.some.convert({ cap: false })((child, i) => {
    if (uuid === child.id) {
      path = [String(i)];
      return true;
    }
    if (child.type === "cell") {
      return false;
    }
    const aPath = getLayoutPath({ uuid, layouts: child });
    if (_3.isEmpty(aPath)) return false;
    path = _3.concat(String(i), aPath);
    return true;
  }, layouts.content);
  return path;
};
var addDocumentToLayout = ({
  layoutId,
  position = 0,
  layouts,
  uuid
}) => addLayoutToLayout({
  layoutId,
  position,
  layouts,
  layout: createCardWithUuid({ uuid }),
  isCell: true
});
var addLayoutToLayout = ({
  layoutId,
  position = 0,
  layouts,
  layout,
  isCell: isCell2 = false
}) => {
  const layoutPath = getLayoutPath({ uuid: layoutId, layouts });
  if (_3.isEmpty(layoutPath) && isCell2) {
    if (_3.isEmpty(layouts)) {
      const newContent = [
        {
          id: getUniqueId(),
          root: true,
          type: ITEMS_TYPES.VGRID,
          content: [
            {
              type: ITEMS_TYPES.VGRID,
              content: [layout],
              id: getUniqueId()
            }
          ]
        }
      ];
      return _3.set("content", newContent, layouts);
    }
    layoutPath.push("0", "0");
  }
  const subPath = _3.isEmpty(layoutPath) ? "content" : `${subStringPath({ layoutPath })}.content`;
  const content = _3.getOr([], subPath, layouts);
  const pos = position === "atEnd" ? _3.size(content) : position;
  const newLayout = _3.set(
    `${subPath}`,
    [..._3.slice(0, pos, content), layout, ..._3.slice(pos, _3.size(content), content)],
    layouts
  );
  return findSiblingsAndRemoveWidth(layout.id, newLayout);
};
var addDocumentToLayouts = ({
  screenModeToPosition = {},
  layouts,
  uuid
}) => {
  return _3.flow(
    _3.map((screenMode) => {
      const layout = _3.get(screenMode, layouts);
      return [
        screenMode,
        typeof layout === "undefined" ? void 0 : addDocumentToLayout({
          layoutId: screenModeToPosition?.[screenMode]?.parentLayoutId ?? "0",
          position: screenModeToPosition?.[screenMode]?.position ?? 0,
          layouts: layout,
          uuid
        })
      ];
    }),
    _3.filter(0),
    _3.fromPairs
  )(["grid-xs", "grid-md"]);
};
var addDocumentsToLayoutsFromParentId = ({
  layoutId,
  layouts,
  uuid,
  parentLayoutId: iParentLayoutId,
  addToTop
}) => {
  if (_3.isEmpty(layouts)) {
    return {};
  }
  if (!uuid || !_3.isString(uuid)) {
    return layouts;
  }
  const screenModeToPosition = _3.flow(
    () => ["grid-xs", "grid-md"],
    _3.map((screenMode) => {
      const aLayouts = _3.get(screenMode, layouts);
      const depth = _3.get(screenMode, SCREENMODE_DEPTH);
      if (_3.isNil(aLayouts)) return [screenMode, {}];
      const [parentLayoutId, position = 0] = layoutId ? findParentLayoutId({ uuid: layoutId, layouts: aLayouts }) : addToTop ? [typeof aLayouts === "undefined" ? void 0 : getFirstLayoutId({ layouts: aLayouts }), void 0] : _3.flow(
        () => aLayouts,
        (layout) => getLastLayoutFromLevel({ layout, depth }),
        (lastLayout) => [lastLayout?.id, _3.size(lastLayout?.content)]
      )();
      return [
        screenMode,
        { parentLayoutId: iParentLayoutId || parentLayoutId, position: iParentLayoutId ? 0 : position }
      ];
    }),
    _3.fromPairs
  )();
  return addDocumentToLayouts({ screenModeToPosition, layouts, uuid });
};
var createCardWithUuid = ({
  autoheight = false,
  uuid
}) => {
  return {
    type: "cell",
    id: uuid,
    autoheight
  };
};
var getLastLayoutFromLevel = ({
  layout,
  depth = 0
}) => {
  return _3.flow(_3.get("content.0"), ..._3.times(() => _3.flow(_3.get("content"), _3.last), depth))(layout);
};
var flattenLayouts = (layouts, screenMode) => {
  const layout = layouts?.[screenMode];
  const uuidToLayoutItem = {};
  const rec = (el) => {
    if (!el) {
      return;
    }
    if (el.type === "cell") {
      uuidToLayoutItem[el.id] = el;
      return;
    }
    if (!_3.isArray(el.content)) {
      return;
    }
    return _3.forEach(rec, el.content);
  };
  rec(layout);
  return uuidToLayoutItem;
};

// src/utils/mode.ts
import _4 from "lodash/fp.js";

// src/constants/index.ts
var VIEWER_CLASSNAME = "lls-new-viewer";
var VIEWER_COMPONENT_CLASSNAME_PREFIX = `${VIEWER_CLASSNAME}-`;
var DOCUMENT_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}document-root`;
var LAYER_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}layer`;
var MATH_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}math`;
var RIGHT_RESPONSE_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}rightresponse`;
var CARD_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}card`;
var CELL_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}cell`;
var VGRID_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}vgrid`;
var HGRID_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}hgrid`;
var RESIZER_WRAPPER_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}resizerwrapper`;
var VGRID_REMOVE_BUTTON_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}vgridremovebutton`;
var VGRID_SORTABLE_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}vgridsortable`;
var TOOLTIP_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}tooltip`;
var VLIST_FIRSTITEM_CLASSNAME = `${VIEWER_COMPONENT_CLASSNAME_PREFIX}vlistfirstitem`;

// src/utils/string.ts
import _5 from "lodash/fp.js";
import murmur from "murmurhash";
var truncate = _5.curry(({ length }, str) => {
  if (!_5.isString(str)) {
    return "";
  }
  const tagRegex = /((?:<[^>]+>)|(?:[^<]+))/;
  const tokens = _5.flow(_5.split(tagRegex), _5.compact)(str);
  const keptTokens = [];
  const openTags = [];
  let consumed = 0;
  for (const token of tokens) {
    if (token.match(/<[^>]+>/)) {
      _5.includes("/", token) ? openTags.pop() : openTags.push(token);
      keptTokens.push(token);
      continue;
    }
    const nextLength = consumed + token.length;
    if (nextLength <= length) {
      consumed += token.length;
      keptTokens.push(token);
      continue;
    }
    keptTokens.push(_5.truncate({ length: length - consumed }, token));
    break;
  }
  return _5.join("", keptTokens) + _5.flow(_5.reverse, _5.map(_5.replace("<", "</")), _5.join(""))(openTags);
});
var removeAttributesFromTags = (tag) => {
  switch (true) {
    case _5.includes("picturesession", tag):
      return _5.replace(/<picturesession.*?src="(.*?)".*?>/gi, '<picturesession src="$1">', tag);
    // keep src attr only for picture session
    case _5.includes("audiosession", tag):
      return _5.replace(/<audiosession.*?src="(.*?)".*?>/gi, '<audiosession src="$1">', tag);
    default:
      return _5.replace(/<\s*(\w+).*?>/gi, "<$1>", tag);
  }
};
var macSanitize = (str) => {
  return _5.isString(str) ? _5.flow(
    // remove mac NFD...
    (str2) => str2.normalize("NFC"),
    // see table https://www.utf8-chartable.de/unicode-utf8-table.pl?number=1024
    // biome-ignore lint/suspicious/noMisleadingCharacterClass:
    _5.replace(/([cdjlmnostu])([\u0312-\u0315]|\u0301)/giu, (_17, cap) => `${cap}'`),
    // combining comma, resp accent use as quote
    // lls-ignore noMacChars:
    _5.replace(/[´’]/g, "'")
  )(str) : str;
};
var hash = (s) => murmur(s).toString(16);
var sanitize = (str) => _5.flow(
  (str2) => str2?.replaceAll(/<stamp[^>]*>.*?<\/stamp>/gs, ""),
  (str2) => str2?.replaceAll(/<picture[^>]*\/>|>.*?<\/picture>/gs, ""),
  (str2) => str2?.replaceAll(/(<math[^>]*>)(.*?)(<\/math>)/gs, ""),
  // $1<![CDATA[$2]]>$3
  (str2) => str2?.replaceAll("&#8209;", "-"),
  (str2) => str2?.replaceAll("&nbsp;", " "),
  _5.unescape,
  _5.trim
)(str);
var getSanitizedContentMd = (contentMd) => {
  if (typeof window === "undefined" && typeof globalThis !== "undefined" && !!globalThis.cheerio) {
    const $ = globalThis.cheerio.load(sanitize(contentMd));
    const parsedText2 = $("body").text();
    return _5.trim(parsedText2);
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitize(contentMd), "application/xml");
  if (doc.querySelector("parsererror")) return;
  const parsedText = doc.documentElement.textContent || "";
  return _5.trim(parsedText);
};
var getHeaderTitle = (contentMd) => {
  const header = contentMd?.match(/<header[^>]*>(.*?)<\/header>/s)?.[0];
  return getSanitizedContentMd(header);
};
var getImageTitle = (contentMd) => contentMd?.match(/<picture[^>]*\salt=["']([^"']*)["']/)?.[1];
var extractTitleFromContentMd = (resource, fallback = `Carte de la page ${resource?.page?.title}`) => {
  const contentMd = resource?.contentMd;
  if (!_5.isString(contentMd)) return;
  const headerTitle = getHeaderTitle(contentMd);
  const contentTitle = getSanitizedContentMd(contentMd);
  const imageTitle = getImageTitle(contentMd);
  const title = headerTitle || contentTitle || imageTitle || fallback;
  return _5.truncate({ length: 50 }, title);
};

// src/utils/range.js
import _6 from "lodash/fp.js";
import { createRoot } from "react-dom/client";
var asHtmlString = _6.get("outerHTML");

// src/utils/maths.ts
import { pipe } from "@lls/utils";
import _7 from "lodash/fp.js";
import round from "lodash/round.js";

// src/utils/response.js
import _8 from "lodash/fp.js";
var getRightResponses = _8.getOr({}, "rightResponses.0");
var getRightResponsesContent = _8.flow(getRightResponses, _8.values, _8.flatten);
var cleanSpaces = _8.flow(_8.replace(/&nbsp;/g, " "), _8.replace(/ /g, " "));
var cleanResponse = _8.flow(_8.trim, _8.toLower, _8.deburr, removeAttributesFromTags, cleanSpaces, macSanitize);
var getSessionExerciseRightResponses = _8.flow(
  _8.cond([
    [_8.flow(_8.get("children"), _8.isEmpty), _8.get("rightResponses")],
    [_8.stubTrue, _8.get("children.0.rightResponses")]
    // Problems (rubrics)isResponseRight
  ])
);

// src/utils/tag.ts
import _9 from "lodash/fp.js";
import HtmlToReact from "html-to-react";
var htmlToReactParser = new HtmlToReact.Parser({
  recognizeSelfClosing: true
});
var buildAttributes = _9.flow(
  _9.toPairs,
  _9.map(([key, value]) => `${key}="${value}"`),
  _9.join(" ")
);
var buildTag = (node, nodeChildren, options = {}) => {
  const tag = _9.getOr("", "tagName", node);
  if (!tag || tag === CONTENT_MD_TAG_NO_TEXT && (!options.allow || !options.allow(node))) {
    return null;
  }
  const attributes = _9.getOr({}, "attribs", node);
  const spaceBeforeAttributes = _9.isEmpty(attributes) ? "" : " ";
  const children = nodeChildren ? _9.join("", nodeChildren) : _9.flow(
    () => node,
    _9.getOr([], "childNodes"),
    _9.map((child) => buildContentMdByNode(child, null, options)),
    _9.join("")
  )();
  if (tag === "unknow") return children;
  return _9.isEmpty(children) ? `<${tag}${spaceBeforeAttributes}${buildAttributes(attributes)} />` : `<${tag}${spaceBeforeAttributes}${buildAttributes(attributes)}>${children}</${tag}>`;
};
var findLightAttributes = (node, { include: iInclude }) => {
  const include = _9.keyBy(_9.identity, iInclude);
  const attributePaths = {};
  let allAttributesFound = false;
  const serializeAttrName = _9.replace(CONTENT_MD_DATA_ATTRIBUTE_PREFIX, "", CONTENT_MD_ATTR_SERIALIZE);
  const recurse = (node2, path = "childNodes") => {
    _9.forEach.convert({ cap: false })((child, i) => {
      if (allAttributesFound) return;
      const attr = _9.get(["attribs", serializeAttrName], child);
      const childPath = `${path}.${i}`;
      if (attr) {
        attributePaths[attr] = childPath;
        delete include[attr];
        allAttributesFound = _9.isEmpty(include);
        return;
      }
      if (typeof child === "string") return;
      if (child.type === "tag" && child.tagName !== "unknow") {
        return;
      }
      return recurse(child, `${childPath}.childNodes`);
    }, node2.childNodes);
  };
  recurse(node);
  return attributePaths;
};
var buildTagWithAttributes = ({
  node,
  children,
  options,
  attributes
}) => {
  const lightAttributePaths = findLightAttributes(node, { include: attributes });
  const attribs = _9.mapValues((path) => {
    const contentMd = _9.flow(
      _9.get(`${path}.childNodes`),
      _9.map(buildContentMdByNode),
      _9.join(""),
      escapeAttribute
    )(node);
    return contentMd;
  }, lightAttributePaths);
  const backNode = _9.flow(
    _9.set("attribs", { ...node.attribs, ...attribs }),
    _9.omit(_9.values(lightAttributePaths)),
    _9.set("tagName", node.tagName)
    // omit is only getting enumerable properties, so we need to set back the non enumerable ones we need to keep
  )(node);
  return buildTag(backNode, children, options);
};
var buildContentMdByNode = (node, children, options) => {
  if (!node) return null;
  const getTagNameNodeValue = _9.flow(
    _9.get("childNodes"),
    _9.find({ tagName: CONTENT_MD_TAG_BACKUP }),
    _9.get("childNodes.0.nodeValue"),
    _9.unescape
  );
  switch (true) {
    case _9.get("attribs.uselightbackup", node) === "1": {
      const txt = getTagNameNodeValue(node);
      return buildTag(node, [txt], options);
    }
    case node.tagName === "picturesession": {
      return buildTag(node, [], options);
    }
    case node.tagName === "audiosession": {
      return buildTag(node, [], options);
    }
    case (node.tagName === "help" && !_9.isEmpty(node.attribs.title)): {
      return buildTagWithAttributes({ node, children, options, attributes: ["title"] });
    }
    case (node.tagName === "trick" && !_9.isEmpty(node.attribs.title)): {
      return buildTagWithAttributes({ node, children, options, attributes: ["title"] });
    }
    case node.tagName === "stamp": {
      const next2 = _9.flow(_9.map(buildContentMdByNode), _9.join(""))(node.childNodes);
      return buildTag(node, _9.flow(_9.trim, _9.size)(next2) ? [next2] : [], options);
    }
    case node.tagName === "questionlabel": {
      return _9.flow(_9.map(buildContentMdByNode), _9.join(""))(node.childNodes);
    }
    case node.tagName === "qc": {
      const next2 = _9.flow(_9.map(buildContentMdByNode), _9.join(""))(node.childNodes);
      return `<label>${buildTag(node, [], options)}${next2}</label>`;
    }
    case node.tagName === "qr": {
      const next2 = _9.flow(_9.map(buildContentMdByNode), _9.join(""))(node.childNodes);
      return `<label>${buildTag(node, [], options)}${next2}</label>`;
    }
    case node.type === "tag":
      return buildTag(node, children, options);
    case node.type === "text":
      return _9.getOr(null, "nodeValue", node);
    default:
      return null;
  }
};
var buildDraggableNodeContentMd = (node) => _9.flow(() => node, _9.set("attribs", {}), buildContentMdByNode, cleanResponse)();
var isQuestionTag = _9.flow(_9.get("name"), (name) => _9.includes(name, QUESTION_TAGS));
var isAnswerTag = _9.matches({ name: "answers" });
var buildSessionLinkNodeContentMd = _9.flow(
  buildDraggableNodeContentMd,
  _9.replace(/<qlink/g, "<draggable"),
  _9.replace(/<\/qlink>/g, "</draggable>")
);
var hasCardRegex = () => /<t?card/;
var hasFullscreen = (contentMd = "") => {
  return hasCardRegex().test(contentMd);
};

// src/utils/template.ts
import dayjs from "dayjs";
import _10 from "lodash/fp.js";
import { nanoid } from "nanoid";
var newCardId = () => -1 * Number(_10.uniqueId("") + 1);
var getDocumentUniqueId = (doc) => _10.get("uuid", doc) || _10.get("id", doc);
var addChildren = ({
  template,
  children = [{}],
  addToTop = false,
  topY,
  layoutId,
  parentLayoutId
}) => {
  !_10.isNil(topY) && console.log("/! topY is deprecated for the addChildren function ");
  const pathChildren = "children";
  const pathLayouts = "layouts";
  const updates = _10.map(
    (child) => (prevTemplate) => {
      const layouts = _10.get(pathLayouts, prevTemplate);
      const card = _10.get("card", child) || child || {};
      const templateChildren = _10.getOr([], pathChildren, prevTemplate);
      const templateHasCard = _10.some({ uuid: card.uuid }, templateChildren);
      const uuid = templateHasCard || !_10.has("uuid", card) ? nanoid() : card.uuid;
      const newChild = _10.assign({ id: newCardId(), uuid }, card);
      const newChildren = _10.concat(templateChildren, newChild);
      const newTemplate = _10.set(pathChildren, newChildren, prevTemplate);
      const newLayouts = addDocumentsToLayoutsFromParentId({ layoutId, parentLayoutId, layouts, uuid, addToTop });
      return _10.set(pathLayouts, newLayouts, newTemplate);
    },
    children
  );
  return _10.flow(updates)(template);
};
var hasRightResponses = (template, filterIds) => {
  return (_10.isUndefined(filterIds) || _10.includes(_10.get("uuid", template), filterIds)) && _10.flow(
    () => template,
    _10.get("rightResponses"),
    _10.some(
      _10.some((response) => !_10.isEmpty(response) || _10.isBoolean(response))
      /* isEmpty(true) => true */
    )
  )();
};
var contentMdHasTag = (contentMd = "", tagName = "nevermatch") => new RegExp(`<${tagName}`).test(contentMd);
var hasQuestionTag = (template) => {
  const content = _10.get("contentMd", template);
  return _10.some((questionTag) => _10.includes(questionTag, content), QUESTION_TAGS);
};
var hasTemplateRightResponses = (tpl) => _10.flow(() => tpl, _10.get("children"), _10.some(hasRightResponses))();
var isAnsweredCard = (docJson) => _10.flow(() => docJson, _10.get("responses"), _10.negate(_10.isEmpty))();
var isCell = (layoutItem) => _10.matches({ type: ITEMS_TYPES.CELL }, layoutItem);
var isVGrid = _10.matches({ type: ITEMS_TYPES.VGRID });
var isVGridRoot = _10.matches({ type: ITEMS_TYPES.VGRID, root: true });
var atLeastOneChildLayoutHasSomeCell = _10.overEvery([
  _10.negate(isVGridRoot),
  _10.flow(_10.get("content"), _10.some(_10.flow(_10.get("content"), _10.some(isCell))))
]);
var filterChildrenWithoutResponses = (template = {}, answeredUuids = void 0) => ({
  ...template,
  children: _10.isNil(answeredUuids) ? _10.flow(() => template, _10.get("children"), _10.filter(isAnsweredCard))() : _10.flow(
    () => template,
    _10.get("children"),
    _10.filter((child) => _10.includes(child.uuid, answeredUuids))
  )()
});
var removeCellsFromLayouts = (childrenUuidsToRemove = []) => (layout = { id: "", type: "cell" }) => {
  const shouldBeRemoved = _10.includes(layout.id, childrenUuidsToRemove);
  if (layout?.type === "cell") return shouldBeRemoved ? void 0 : layout;
  const content = _10.flow(
    () => layout,
    _10.get("content"),
    _10.map(removeCellsFromLayouts(childrenUuidsToRemove)),
    _10.compact
  )();
  return {
    ...layout,
    content
  };
};
var removeGridsFromLayouts = (hasSiblings = false) => (layout = { id: "string", type: "cell" }) => {
  if (layout.type === "cell") return layout;
  const newContent = _10.flow(
    () => layout,
    _10.get("content"),
    _10.map(removeGridsFromLayouts(atLeastOneChildLayoutHasSomeCell(layout))),
    _10.compact
  )();
  return !_10.isEmpty(newContent) || isVGrid(layout) && hasSiblings ? {
    ...layout,
    content: newContent
  } : void 0;
};
var filterLayout = (layoutName, childrenUuidsToRemove) => (template = {}) => ({
  ...template,
  layouts: {
    ..._10.getOr({}, "layouts", template),
    [layoutName]: _10.flow(
      () => template,
      _10.get(["layouts", layoutName]),
      removeCellsFromLayouts(childrenUuidsToRemove),
      removeGridsFromLayouts(false)
    )()
  }
});
var filterLayoutsWithoutResponses = (template, answeredUuids) => {
  const childrenUuidsToRemove = _10.flow(
    _10.get("children"),
    (children) => _10.isNil(answeredUuids) ? _10.reject(isAnsweredCard, children) : _10.filter((child) => !_10.includes(child.uuid, answeredUuids), children),
    _10.map("uuid")
  )(template);
  return _10.flow(
    () => template,
    filterLayout("grid-xs", childrenUuidsToRemove),
    filterLayout("grid-md", childrenUuidsToRemove)
  )();
};
var getAnsweredOnlyTemplate = (tpl) => _10.flow(() => tpl, filterLayoutsWithoutResponses, filterChildrenWithoutResponses)();
var getExercicesOnlyTemplate = (template) => {
  const children = _10.filter((child) => hasQuestionTag(child), template.children);
  return _10.set("children", children, template);
};
var getAnsweredExercisesTemplate = (template, uuids) => {
  const children = _10.filter((child) => _10.includes(child.uuid, uuids), template.children);
  return _10.set("children", children, template);
};
var getShareCorrectionTemplates = (answeredUuids = [], template = {}) => {
  const templateWithOnlyExercises = getExercicesOnlyTemplate(template);
  const pickedTemplate = getAnsweredExercisesTemplate(templateWithOnlyExercises, answeredUuids);
  return {
    template: {
      ...templateWithOnlyExercises,
      contentMd: templateWithOnlyExercises?.isPartOfSession ? "<session/>" : "<grid/>"
    },
    pickedTemplate
  };
};
var fillPageWithEmptyTemplate = ({
  page = {},
  type = "page",
  userName = "",
  chapterTitle = ""
} = {}) => {
  const title = `${type === "exercice" ? "Fiche d'exercices" : "Ma page"} du ${dayjs().format("DD/MM")}`;
  const stamp = type === "exercice" ? "Exercices" : `Page de ${userName}`;
  const description = escapeAttribute(
    type === "exercice" ? `Exercices sur la th\xE9matique "${chapterTitle}"` : "Description de la page"
  );
  const illu = type === "exercice" ? "mat-nombres-calculs" : "methode";
  return {
    ...page,
    title,
    templateLoaded: true,
    pageType: type,
    layouts: {
      "grid-xs": {
        content: [
          {
            id: getUniqueId(),
            root: true,
            type: "vgrid",
            content: [
              {
                type: "vgrid",
                content: [],
                id: getUniqueId()
              }
            ]
          }
        ]
      },
      "grid-md": {
        content: [
          {
            id: getUniqueId(),
            root: true,
            type: "vgrid",
            content: [
              {
                type: "vgrid",
                content: [
                  {
                    type: "hgrid",
                    content: [
                      {
                        type: "vgrid",
                        content: [],
                        id: getUniqueId()
                      }
                    ],
                    id: getUniqueId()
                  }
                ],
                id: getUniqueId()
              }
            ]
          }
        ]
      }
    },
    contentMd: `<mainheader cover="${illu}">
 <stamp size="big" data-clarity-mask='true'>${stamp}</stamp>
<h1 placeholder="${title}"></h1>
<h3 placeholder="${description}"></h3>
</mainheader>
<grid/>`,
    children: []
  };
};
var getFullscreenableDocsUuidGroupedByTabsOrWorkplan = (docJson, screenMode) => {
  const tabOrWorkplanLayouts = _10.flow(
    () => docJson,
    _10.get(["layouts", screenMode]),
    // @ts-ignore
    (layouts) => findLayout({ layouts, matchFn: _10.overSome([{ type: "tabs" }, { type: "workplan" }]) }),
    _10.get("content")
  )();
  const uuidToChild = _10.flow(
    () => docJson,
    _10.get("children"),
    _10.map((child) => hasFullscreen(child.contentMd) ? child : void 0),
    _10.compact,
    _10.keyBy("uuid")
  )();
  if (!tabOrWorkplanLayouts) {
    const uuidToLayoutItem = flattenLayouts(docJson?.layouts, screenMode);
    return _10.flow(
      () => uuidToLayoutItem,
      _10.keys,
      _10.flatMap((uuid) => _10.has(uuid, uuidToChild) ? uuid : [])
    )();
  }
  return {
    groups: _10.flow(
      () => tabOrWorkplanLayouts,
      _10.map((tabLayout) => {
        const uuidToLayoutItem = flattenLayouts({ [screenMode]: tabLayout }, screenMode);
        return [
          tabLayout.id,
          _10.flow(
            () => uuidToLayoutItem,
            _10.keys,
            _10.flatMap((uuid) => _10.has(uuid, uuidToChild) ? uuid : [])
          )()
        ];
      }),
      _10.fromPairs
    )()
  };
};

// src/constants/lightEditor.ts
var LIGHT_EDITOR_ONCHANGE_DEFAULT_CONTEXT = "light-editor-onchange";
var LIGHT_EDITOR_ONCHANGE_TEXT_CONTEXT = "light-editor-onchange-text";
var LIGHT_EDITOR_ONCHANGE_EDIT_LAYOUT_CONTEXT = "light-editor-onchange-edit-layout";

// src/utils/history.js
import _11 from "lodash/fp.js";
var push = ({ items, index }, item) => ({ items: items.slice(0, index + 1).concat(item), index: index + 1 });
var prev = ({ items, index }) => ({ items, index: Math.max(0, index - 1) });
var next = ({ items, index }) => ({ items, index: Math.min(_11.size(items) - 1, index + 1) });
var hasNext = ({ items, index }) => index !== _11.size(items) - 1;
var hasPrev = ({ index }) => index > 0;

// src/hooks/useViewerHistory.jsx
import _12 from "lodash/fp.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
var useViewerHistory = ({ template, onChange = _12.noop }) => {
  const historyRef = useRef();
  const [lightTouchedIds, setLightTouchedIds] = useState({});
  const id = getDocumentUniqueId(template);
  const activeTemplate = _12.get(["current", "items", 0, "template"], historyRef);
  if (id !== getDocumentUniqueId(activeTemplate) || !historyRef.current) {
    historyRef.current = { items: [{ template, touchedId: null }], index: 0 };
  }
  useEffect(() => {
    _12.noop(id);
    setLightTouchedIds({});
  }, [id]);
  const onMove = useCallback(
    (moveFn) => {
      const { touchedId } = historyRef.current.items[historyRef.current.index];
      historyRef.current = moveFn(historyRef.current);
      const { template: template2 } = historyRef.current.items[historyRef.current.index];
      touchedId && setLightTouchedIds({ [touchedId]: Date.now() });
      onChange(template2, LIGHT_EDITOR_ONCHANGE_DEFAULT_CONTEXT);
    },
    [onChange]
  );
  const onGoBack = useCallback(() => onMove(prev), [onMove]);
  const onGoForward = useCallback(() => onMove(next), [onMove]);
  const addEntry = useCallback((template2, id2) => {
    historyRef.current = push(historyRef.current, { template: template2, touchedId: id2 });
  }, []);
  const hasPreviousEntry = hasPrev(historyRef.current);
  const hasNextEntry = hasNext(historyRef.current);
  const historyProps = useMemo(() => {
    return { lightTouchedIds, addEntry, hasPreviousEntry, hasNextEntry, onGoBack, onGoForward };
  }, [lightTouchedIds, addEntry, hasPreviousEntry, hasNextEntry, onGoBack, onGoForward]);
  return historyProps;
};
var useViewerHistory_default = useViewerHistory;

// src/hooks/usePresentation.tsx
import {
  PRESENTATION_EVENTS,
  dispatchAnalyticsEvent,
  enterBrowserFullscreen,
  exitBrowserFullscreen,
  isBrowserInFullscreen
} from "@lls/utils";

// src/constants/analytics.ts
var ANALYTICS_EVENTS_CONTENT_VIEW_CHANGED = "content_view_changed";
var ANALYTICS_VIEW_PRESENTATION_DOC = "presentation_doc";

// src/constants/projector.ts
var SPLITVIEW_LEFT_POSITION = 0;
var SPLITVIEW_RIGHT_POSITION = 1;
var FULLSCREEN_ANIM_TIME = 250;
var PRESENTATION_VIEW_PAGE = "page";
var PRESENTATION_VIEW_SLIDE = "slide";
var PRESENTATION_VIEW_SPLIT = "split";

// src/hooks/useLastFnByPageId.ts
import { useCallback as useCallback2, useEffect as useEffect2, useRef as useRef2 } from "react";
var useLastFnByPageId = (fn, page) => {
  const pageIdToFnRef = useRef2({});
  const pageId = page?.id ?? "";
  pageIdToFnRef.current[pageId] = { fn };
  useEffect2(() => {
    const hmap = pageIdToFnRef.current;
    return () => {
      delete hmap[pageId];
    };
  }, [pageId]);
  return useCallback2(
    (...args) => {
      const pageFn = pageIdToFnRef.current[pageId];
      if (!pageFn) return;
      return pageFn?.fn(...args);
    },
    [pageId]
  );
};
var useLastFnByPageId_default = useLastFnByPageId;

// src/hooks/useMemoObject.jsx
import { useRef as useRef3 } from "react";
var useMemoObject = (object, compareFunction = (old, next2) => JSON.stringify(old) === JSON.stringify(next2)) => {
  const objectRef = useRef3(object);
  if (!compareFunction(objectRef.current, object)) {
    objectRef.current = object;
  }
  return objectRef.current;
};
var useMemoObject_default = useMemoObject;

// src/hooks/useZoom.jsx
import { useMemo as useMemo2, useState as useState2 } from "react";
var ZOOM_SCALE = [25, 33, 50, 67, 75, 80, 90, 100, 110, 125, 150, 175, 200, 250, 300, 400, 500];
var DEFAULT_STEP = 7;
var useZoom = () => {
  const [zoomStep, setZoomStep] = useState2(void 0);
  const zoomProps = useMemo2(() => {
    const resetZoom = (resetStep) => {
      if (resetStep) return setZoomStep(resetStep);
      if (!window) return setZoomStep(DEFAULT_STEP);
      const currentScreenWidth = window.innerWidth;
      switch (true) {
        case currentScreenWidth <= 800:
          setZoomStep(9);
          break;
        case currentScreenWidth <= 1024:
          setZoomStep(10);
          break;
        case currentScreenWidth <= 1600:
          setZoomStep(12);
          break;
        case currentScreenWidth <= 1920:
          setZoomStep(13);
          break;
        case currentScreenWidth <= 2560:
          setZoomStep(14);
          break;
        default:
          setZoomStep(15);
          break;
      }
    };
    const increaseZoom = () => {
      setZoomStep((prev2) => Math.min(prev2 + 1, ZOOM_SCALE.length - 1));
    };
    const decreaseZoom = () => {
      setZoomStep((prev2) => Math.max(prev2 - 1, 0));
    };
    return { zoomStep, zoom: ZOOM_SCALE[zoomStep], resetZoom, increaseZoom, decreaseZoom };
  }, [zoomStep]);
  return zoomProps;
};
var useZoom_default = useZoom;

// src/utils/browser.ts
var isIOS = () => isSsr() ? false : /iPad|iPhone/.test(navigator.userAgent);
var isSafari = () => {
  if (isSsr() || navigator.userAgent.match(/chrome|chromium|crios|firefox|fxios|opr|edg/i)) return false;
  return !!navigator.userAgent.match(/safari/i);
};
var isSafariIOs = () => isIOS() && isSafari();

// src/utils/view.ts
var isProjectorView = (presentationProps) => !!presentationProps?.presentationView?.view;

// src/hooks/usePresentation.tsx
import _13 from "lodash/fp.js";
import { useCallback as useCallback3, useEffect as useEffect3, useMemo as useMemo3, useRef as useRef4, useState as useState3 } from "react";
var usePresentation = (docJson = {}, iSendTourEvent = _13.noop) => {
  const [presentationView, setPresentationView] = useState3({
    view: void 0
  });
  const [preSlideDocUuid, setPreSlideDocUuid] = useState3(void 0);
  const [isLaserCursor, setIsLaserCursor] = useState3(false);
  const [zoomModalUuids, setZoomModalUuids] = useState3([]);
  const viewerZoomProps = useZoom_default();
  const leftDocZoomProps = useZoom_default();
  const rightDocZoomProps = useZoom_default();
  const sendTourEvent = useLastFnByPageId_default(iSendTourEvent, docJson);
  const onFullScreenChangeRef = useRef4(null);
  const initZoomModal = useCallback3((docUuid) => {
    setZoomModalUuids((prev2) => _13.includes(docUuid, prev2) ? prev2 : [...prev2, docUuid]);
  }, []);
  const deleteZoomModal = useCallback3((docUuid) => {
    setZoomModalUuids(_13.pull(docUuid));
  }, []);
  const closeFullscreenCard = useCallback3((docUuid) => {
    setPresentationView((old) => {
      if (!old?.view) return old;
      if (_13.isUndefined(docUuid) || old?.view !== PRESENTATION_VIEW_SPLIT || old?.uuids?.includes(void 0))
        return { view: PRESENTATION_VIEW_PAGE };
      return { view: PRESENTATION_VIEW_SPLIT, uuids: old?.uuids?.map((entry) => entry === docUuid ? void 0 : entry) };
    });
  }, []);
  const docsUuidWithFullscreenEnabled = useMemoObject_default(
    getFullscreenableDocsUuidGroupedByTabsOrWorkplan(docJson, "grid-xs")
  );
  const presentationProps = useMemo3(() => {
    const { zoomStep: viewerZoomStep, resetZoom: resetViewerZoom } = viewerZoomProps;
    const { zoomStep: leftDocZoomStep, resetZoom: resetLeftDocZoom } = leftDocZoomProps;
    const { zoomStep: rightDocZoomStep, resetZoom: resetRightDocZoom } = rightDocZoomProps;
    const getActiveChildrenUuids = (tabId) => docsUuidWithFullscreenEnabled?.groups && tabId !== void 0 ? docsUuidWithFullscreenEnabled?.groups?.[tabId] : docsUuidWithFullscreenEnabled;
    const getChildPosition = (docUuid, tabId) => {
      return getActiveChildrenUuids(tabId)?.findIndex((uuid) => uuid === docUuid) ?? -1;
    };
    const getNbDocs = (tabId) => {
      return getActiveChildrenUuids(tabId)?.length;
    };
    const getFirstDocWithFullscreen = (tabId) => {
      const tab = tabId ?? docsUuidWithFullscreenEnabled?.groups ? Object.keys(docsUuidWithFullscreenEnabled.groups)?.[0] : void 0;
      return getActiveChildrenUuids(tab)?.[0];
    };
    const closeProjectorView = () => {
      if (onFullScreenChangeRef.current === null || isSafariIOs()) return;
      setIsLaserCursor(false);
      sendTourEvent("close_projector_view");
      const maybeWindow = getWindow();
      if (!("document" in maybeWindow)) return;
      for (const event of PRESENTATION_EVENTS) {
        maybeWindow.document?.removeEventListener(event, onFullScreenChangeRef.current);
      }
      onFullScreenChangeRef.current = null;
      setPresentationView({ view: void 0 });
      if (isBrowserInFullscreen()) exitBrowserFullscreen();
    };
    const onFullScreenChange = () => {
      if (!isBrowserInFullscreen()) closeProjectorView();
    };
    const openProjectorView = () => {
      if (!isBrowserInFullscreen() && !isSafariIOs()) {
        enterBrowserFullscreen();
        const maybeWindow = getWindow();
        if (!("document" in maybeWindow)) return;
        if (onFullScreenChangeRef.current)
          for (const event of PRESENTATION_EVENTS) {
            maybeWindow.document?.removeEventListener(event, onFullScreenChangeRef.current);
          }
        onFullScreenChangeRef.current = onFullScreenChange;
        for (const event of PRESENTATION_EVENTS) {
          maybeWindow.document?.addEventListener(event, onFullScreenChangeRef.current);
        }
      }
      resetViewerZoom();
      setPresentationView({ view: PRESENTATION_VIEW_SLIDE, uuid: firstCardUuid });
    };
    const closeSplitView = () => {
      dispatchAnalyticsEvent(ANALYTICS_EVENTS_CONTENT_VIEW_CHANGED, {
        contentViewFormat: ANALYTICS_VIEW_PRESENTATION_DOC
      });
      setPresentationView((old) => {
        if (old?.view !== PRESENTATION_VIEW_SPLIT) return old;
        if (old?.uuids?.[SPLITVIEW_RIGHT_POSITION]) resetLeftDocZoom(rightDocZoomStep);
        return { view: PRESENTATION_VIEW_SLIDE, uuid: old?.uuids?.find((entry) => entry !== void 0) };
      });
    };
    const extendSplitView = (view = SPLITVIEW_LEFT_POSITION) => {
      if (presentationView?.view !== PRESENTATION_VIEW_SPLIT) return;
      const viewToExtend = presentationView?.uuids?.[view];
      if (!viewToExtend) return setPresentationView({ view: PRESENTATION_VIEW_PAGE });
      view === SPLITVIEW_RIGHT_POSITION && resetLeftDocZoom(rightDocZoomStep);
      setPresentationView({ view: PRESENTATION_VIEW_SLIDE, uuid: viewToExtend });
    };
    const updateFullscreenCard = (oldUuid, newUuid) => {
      setPresentationView((old) => {
        return _13.flow(
          (old2) => {
            switch (old2?.view) {
              case PRESENTATION_VIEW_SLIDE:
                return { view: PRESENTATION_VIEW_SLIDE, uuid: newUuid };
              case PRESENTATION_VIEW_SPLIT:
                return {
                  view: PRESENTATION_VIEW_SPLIT,
                  uuids: old2?.uuids?.map((entry) => entry === oldUuid ? newUuid : entry)
                };
              default:
                return old2;
            }
          },
          _13.set("lastOpenUuid", newUuid)
        )(old);
      });
    };
    const slideToAnotherDocument = ({
      currentDocUuid,
      tabId,
      direction = 1
    }) => {
      const currentChildIndex = getActiveChildrenUuids(tabId)?.findIndex((uuid) => uuid === currentDocUuid);
      const slideFn = (step) => updateFullscreenCard(
        currentDocUuid,
        getActiveChildrenUuids(tabId)?.[step > 0 ? Math.min(getActiveChildrenUuids(tabId)?.length - 1, currentChildIndex + step) : Math.max(0, currentChildIndex + step)]
      );
      if (presentationView?.view !== PRESENTATION_VIEW_SPLIT) {
        return slideFn(direction);
      }
      const otherDocUuid = presentationView?.uuids?.find((entry) => entry !== currentDocUuid);
      if (!otherDocUuid && _13.isEmpty(zoomModalUuids)) {
        return slideFn(direction);
      }
      const otherChildIndex = getActiveChildrenUuids(tabId)?.findIndex(
        (uuid) => uuid === otherDocUuid || _13.includes(uuid, zoomModalUuids)
      );
      if (otherChildIndex !== currentChildIndex + direction) {
        return slideFn(direction);
      }
      if (currentChildIndex + direction * 2 >= 0 && currentChildIndex + direction * 2 <= getActiveChildrenUuids(tabId)?.length - 1) {
        slideFn(direction * 2);
      }
    };
    const slideToPreviousDocument = (currentDocUuid, tabId) => slideToAnotherDocument({ currentDocUuid, tabId, direction: -1 });
    const slideToNextDocument = (currentDocUuid, tabId) => slideToAnotherDocument({ currentDocUuid, tabId, direction: 1 });
    const resetLeftDocZoomViewerStep = () => {
      resetLeftDocZoom(viewerZoomStep);
    };
    const openFullscreenCard = (docUuid, forceSplit = false) => {
      viewerZoomStep === void 0 && resetViewerZoom();
      setPresentationView((old) => {
        return _13.flow(
          (old2) => {
            switch (old2?.view) {
              case PRESENTATION_VIEW_SLIDE: {
                resetRightDocZoom(leftDocZoomStep);
                sendTourEvent("open_split_view");
                return {
                  view: PRESENTATION_VIEW_SPLIT,
                  uuids: old2?.uuid === docUuid ? [old2?.uuid, void 0] : [old2?.uuid, docUuid]
                };
              }
              case PRESENTATION_VIEW_SPLIT:
                if (old2?.uuids?.[SPLITVIEW_LEFT_POSITION]) {
                  resetRightDocZoom(viewerZoomStep);
                  return _13.set(["uuids", SPLITVIEW_RIGHT_POSITION], docUuid, old2);
                }
                resetLeftDocZoom(viewerZoomStep);
                return _13.set(["uuids", SPLITVIEW_LEFT_POSITION], docUuid, old2);
              default: {
                if (!isBrowserInFullscreen() && !isSafariIOs()) {
                  enterBrowserFullscreen();
                  const maybeWindow = getWindow();
                  if (!("document" in maybeWindow)) return;
                  if (onFullScreenChangeRef.current)
                    for (const event of PRESENTATION_EVENTS) {
                      maybeWindow.document?.removeEventListener(event, onFullScreenChangeRef.current);
                    }
                  onFullScreenChangeRef.current = onFullScreenChange;
                  for (const event of PRESENTATION_EVENTS) {
                    maybeWindow.document?.addEventListener(event, onFullScreenChangeRef.current);
                  }
                }
                resetLeftDocZoom(viewerZoomStep);
                sendTourEvent("open_fullscreen_card");
                return forceSplit ? { view: PRESENTATION_VIEW_SPLIT, uuids: [docUuid, void 0] } : { view: PRESENTATION_VIEW_SLIDE, uuid: docUuid };
              }
            }
          },
          _13.set("lastOpenUuid", docUuid)
        )(old);
      });
    };
    const openFullscreenCardWithSmoothTransition = (docUuid, forceSplit = false) => {
      if (!presentationView.view) {
        setPreSlideDocUuid(docUuid);
        setTimeout(() => {
          openFullscreenCard(docUuid, forceSplit);
          setPreSlideDocUuid(void 0);
        }, FULLSCREEN_ANIM_TIME + 200);
        return;
      }
      openFullscreenCard(docUuid, forceSplit);
    };
    return {
      isPresentationMode: isProjectorView({ presentationView }),
      isPaginationEnabled: true,
      presentationView,
      preSlideDocUuid,
      viewerZoomProps,
      leftDocZoomProps,
      rightDocZoomProps,
      isLaserCursor,
      setIsLaserCursor,
      openProjectorView,
      extendSplitView,
      closeFullscreenCard,
      closeProjectorView,
      closeSplitView,
      openFullscreenCard: openFullscreenCardWithSmoothTransition,
      slideToPreviousDocument,
      getChildPosition,
      slideToNextDocument,
      initZoomModal,
      deleteZoomModal,
      getFirstDocWithFullscreen,
      getNbDocs,
      resetLeftDocZoomViewerStep
    };
  }, [
    docsUuidWithFullscreenEnabled,
    presentationView,
    preSlideDocUuid,
    viewerZoomProps,
    leftDocZoomProps,
    rightDocZoomProps,
    zoomModalUuids,
    isLaserCursor,
    initZoomModal,
    deleteZoomModal,
    closeFullscreenCard,
    sendTourEvent
  ]);
  const firstCardUuid = presentationProps.getFirstDocWithFullscreen?.();
  const shouldTriggerFullscreenCard = presentationView.view === PRESENTATION_VIEW_PAGE && firstCardUuid;
  useEffect3(() => {
    if (shouldTriggerFullscreenCard) {
      presentationProps.openFullscreenCard?.(firstCardUuid);
    }
  }, [shouldTriggerFullscreenCard, presentationProps.openFullscreenCard, firstCardUuid]);
  useEffect3(() => {
    if (presentationProps?.isPresentationMode && presentationProps.closeProjectorView) {
      document.addEventListener("viewer-close-projector-view", presentationProps.closeProjectorView);
      return () => {
        document.removeEventListener("viewer-close-projector-view", presentationProps.closeProjectorView);
      };
    }
  }, [presentationProps.closeProjectorView, presentationProps?.isPresentationMode]);
  return presentationProps;
};
var usePresentation_default = usePresentation;

// src/utils/ai.js
import _14 from "lodash/fp.js";
var buildAnswer = (questionIndex) => (answer, answerIndex) => {
  return `
    <label><qr id="qr-ai-${questionIndex}" value="qr-ia-${questionIndex}-rep-${answerIndex}" />${answer}</label><br />
  `;
};
var buildQuestion = (question) => {
  return `
    <div>${question?.question}</div><br />
    ${_14.flow(_14.map.convert({ cap: false })(buildAnswer(hash(question.question))), _14.join(""))(question?.answers)}
    <br />
  `;
};
var LOADER = `<center><img src="/assets/ai_loader.gif" alt="loader" />`;
var transformNDJSONToQuestionCheckbox = (jsonChunks = []) => {
  if (_14.isEmpty(jsonChunks)) return {};
  const [titleChunk, ...questionChunks] = jsonChunks;
  return {
    contentMd: `
      <card hidecorrection="true" ><fullscreen /><br />
        <h3>${titleChunk?.title}</h3><br />
        ${_14.flow(_14.map(buildQuestion), _14.join(""))(questionChunks)}
        <br />
        ${LOADER}
      </card>
    `,
    rightResponses: _14.isEmpty(questionChunks) ? [] : [
      _14.reduce(
        (memo, question) => {
          const questionIndex = hash(question.question);
          memo[`qr-ai-${questionIndex}`] = `qr-ia-${questionIndex}-rep-${question.rightAnswer}`;
          return memo;
        },
        {},
        questionChunks
      )
    ]
  };
};
var removeAILoader = (page, uuid) => ({
  ...page,
  children: _14.map(
    (child) => child?.uuid === uuid ? _14.update("contentMd", _14.replace(new RegExp(LOADER, "g"), ""), child) : child,
    page?.children
  )
});

// src/utils/customization.ts
import _15 from "lodash/fp.js";
var isEditionDisabled = (docJson, isInsideSession) => {
  const contentMd = _15.get("contentMd", docJson);
  return isInsideSession || contentMdHasTag(contentMd, "session") || contentMdHasTag(contentMd, "tabs") || contentMdHasTag(contentMd, "timeline") || contentMdHasTag(contentMd, "map");
};

// src/utils/workplan.js
import _16 from "lodash/fp.js";
import { nanoid as nanoid2 } from "nanoid";
var upsertModality = (existingModalities = [], inputModality = void 0) => {
  if (!inputModality) return existingModalities;
  const found = _16.find({ type: _16.get("type", inputModality) }, existingModalities);
  if (found) {
    return _16.map((modality) => {
      if (modality.type === found.type) return _16.assign(modality, inputModality);
      return modality;
    }, existingModalities);
  }
  return [...existingModalities, inputModality];
};
var getWorkplanOptionsFromWorkplan = ({ workplans, teacherId }) => {
  return _16.flow(
    _16.get("0.options"),
    (options) => _16.isNil(teacherId) ? options : _16.filter({ teacherId }, options),
    _16.sortBy(["updatedAt"]),
    _16.sortedUniqBy(({ teacherId: teacherId2, uuid }) => ({ teacherId: teacherId2, uuid })),
    _16.keyBy("uuid")
  )(workplans);
};
var replaceAttributeValue = ({ tagName, attributeName, newAttributeValue, contentMd }) => {
  const rgexAttribute = new RegExp(`(<${tagName}.*${attributeName}\\s*=\\s*")[^"]+(")`, "is");
  return _16.isNil(newAttributeValue) ? contentMd : _16.replace(rgexAttribute, `$1${newAttributeValue}$2`, contentMd);
};
var buildContentMdWithWorkplanOption = ({ contentMd, option }) => {
  const { time, level, work, who, where } = option;
  return _16.flow(
    (contentMd2) => replaceAttributeValue({ tagName: "wpinfos", attributeName: "time", newAttributeValue: time, contentMd: contentMd2 }),
    (contentMd2) => replaceAttributeValue({ tagName: "wpinfos", attributeName: "level", newAttributeValue: level, contentMd: contentMd2 }),
    (contentMd2) => replaceAttributeValue({ tagName: "wpmodalities", attributeName: "work", newAttributeValue: work, contentMd: contentMd2 }),
    (contentMd2) => replaceAttributeValue({ tagName: "wpmodalities", attributeName: "who", newAttributeValue: who, contentMd: contentMd2 }),
    (contentMd2) => replaceAttributeValue({ tagName: "wpmodalities", attributeName: "where", newAttributeValue: where, contentMd: contentMd2 })
  )(contentMd);
};
var simplifyOption = (option) => {
  const { difficulty: level, time } = option;
  return {
    level,
    time,
    ..._16.flow(_16.getOr([], "modalities"), _16.keyBy("type"), _16.mapValues("value"))(option)
  };
};
var updateWorkplanOptionsInPage = ({ template, options }) => {
  if (_16.isEmpty(template)) {
    return {};
  }
  if (_16.isEmpty(options)) {
    return template;
  }
  const optionsWithDate = _16.mapValues((value) => ({ ...value, updatedAt: (/* @__PURE__ */ new Date()).toISOString() }), options);
  const newOptions = _16.flow(_16.getOr([], "workplans.0.options"), _16.unionBy("uuid", _16.values(optionsWithDate)))(template);
  return _16.set("workplans.0.options", newOptions, template);
};
var updateTemplateWithWorkplanOptions = (page) => {
  const options = _16.flow(
    _16.get("workplans"),
    (workplans) => getWorkplanOptionsFromWorkplan({ workplans }),
    _16.mapValues(simplifyOption)
  )(page);
  if (_16.isEmpty(options) || _16.isNil(options)) return page;
  return _16.flow(
    _16.get("children"),
    _16.map((child) => {
      const option = _16.get(_16.get("uuid", child), options);
      return _16.isNil(option) ? child : _16.set("contentMd", buildContentMdWithWorkplanOption({ contentMd: _16.get("contentMd", child), option }), child);
    }),
    (newChildren) => _16.set("children", newChildren, page)
  )(page);
};
var formatWorkplanCardsOptions = ({ workplanCardsOptions, ctx }) => {
  const inputOptions = _16.flow(_16.get("workplanCardOption"), _16.pick(["uuid", "time", "difficulty", "modality"]))(ctx);
  const existingOptions = _16.get(inputOptions.uuid, workplanCardsOptions) || {};
  const { modality, ...otherOptions } = inputOptions;
  const next2 = {
    ...existingOptions,
    ...otherOptions,
    modalities: upsertModality(_16.getOr([], "modalities", existingOptions), modality)
  };
  return _16.set(next2.uuid, next2, workplanCardsOptions);
};
var buildChildrenWithStats = ({ page, stats }) => {
  const children = _16.get("children", page);
  return _16.map((child) => {
    const newChild = _16.flow(_16.filter({ uuid: child.uuid }), _16.maxBy("beginningDate"))(stats);
    return { ...child, responses: newChild ? _16.get("responses", newChild) : [] };
  }, children);
};
var buildPageWithStudentStats = ({ page, stats, studentId }) => {
  const newChildren = buildChildrenWithStats({ page, stats });
  return _16.assign(page, {
    children: newChildren,
    user: studentId
  });
};

// src/staticImports.js
var addChildren2 = (template, children, addToTop, topY, path) => {
  console.warning("addChildren is deprecated, use addChildrenUtil instead");
  return addChildren({ template, children, addToTop, topY, path });
};
var EDITOR_ENTRY = "EditorNext";
var PICKER_ENTRY = "PickerNext";
var LIGHT_EDITOR_ENTRY = "LightEditorNext";
var PREVIEW_ENTRY = "PreviewNext";
var CORRECTION_ENTRY = "CorrectionNext";
var VIEWER_ENTRY = "ViewerNext";
export {
  BREAKPOINTS,
  CORRECTION_ENTRY,
  EDITOR_ENTRY,
  LIGHT_EDITOR_ENTRY,
  LIGHT_EDITOR_ONCHANGE_EDIT_LAYOUT_CONTEXT,
  LIGHT_EDITOR_ONCHANGE_TEXT_CONTEXT,
  PICKER_ENTRY,
  PREVIEW_ENTRY,
  VIEWER_ENTRY,
  addChildren2 as addChildren,
  addChildren as addChildrenUtil,
  buildPageWithStudentStats,
  extractTitleFromContentMd,
  fillPageWithEmptyTemplate,
  findLayout,
  formatWorkplanCardsOptions,
  getAnsweredOnlyTemplate,
  getShareCorrectionTemplates,
  hasTemplateRightResponses,
  isEditionDisabled,
  removeAILoader,
  transformNDJSONToQuestionCheckbox,
  updateTemplateWithWorkplanOptions,
  updateWorkplanOptionsInPage,
  usePresentation_default as usePresentation,
  useViewerHistory_default as useViewerHistory
};
/*
  @license
  ========================================================================

  LICENSED UNDER MIT
  ------------------

  See https://github.com/pvzzombs/simple-math-eval-calc/blob/master/LICENSE for the license
  This is a MIT - licensed version of Sinple Math Eval Calc.
  If you want to see the APACHE - licensed version, go to :
    https://bit.ly/2whs9fi

  Thank You.

  ========================================================================

*/
